import { useState, useEffect, useCallback, useRef } from "react";
import {
  Shield, AlertTriangle, Activity, Clock, Copy, Check,
  ChevronDown, RefreshCw, Search, Filter, XCircle, Link2
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Area, AreaChart
} from "recharts";

const API = `${getApiUrl()}/api`;
import getApiUrl from '../../utils/getApiUrl';

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


const SEVERITY_COLORS = {
  CRITICAL: { bg: "bg-red-950", text: "text-red-400", border: "border-red-800", dot: "#ef4444" },
  HIGH: { bg: "bg-orange-950", text: "text-orange-400", border: "border-orange-800", dot: "#f97316" },
  MEDIUM: { bg: "bg-amber-950", text: "text-amber-400", border: "border-amber-800", dot: "#f59e0b" },
  LOW: { bg: "bg-blue-950", text: "text-blue-400", border: "border-blue-800", dot: "#3b82f6" },
  INFO: { bg: "bg-slate-800", text: "text-slate-400", border: "border-slate-700", dot: "#64748b" },
};

const WINDOWS = [
  { value: "5m", label: "5 min" },
  { value: "1h", label: "1 heure" },
  { value: "24h", label: "24 heures" },
];

function SeverityBadge({ severity }) {
  const c = SEVERITY_COLORS[severity] || SEVERITY_COLORS.INFO;
  return (
    <span data-testid={`severity-badge-${severity}`} className={`px-2 py-0.5 rounded text-xs font-mono font-bold ${c.bg} ${c.text} border ${c.border}`}>
      {severity}
    </span>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={handleCopy} className="p-1 hover:bg-slate-700 rounded transition-colors" title="Copier">
      {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3 text-slate-500" />}
    </button>
  );
}

function KpiCard({ label, value, severity, testId }) {
  const colors = severity === "critical" ? "border-red-800 bg-red-950/40"
    : severity === "high" ? "border-orange-800 bg-orange-950/40"
    : severity === "ok" ? "border-emerald-800 bg-emerald-950/40"
    : "border-slate-700 bg-slate-800/60";
  return (
    <div data-testid={testId} className={`p-4 rounded-lg border ${colors}`}>
      <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">{label}</div>
      <div className="text-2xl font-bold text-white font-mono">{value ?? "—"}</div>
    </div>
  );
}

function MiniChart({ data }) {
  if (!data || data.length === 0) {
    return <div className="h-40 flex items-center justify-center text-slate-600 text-sm">Aucune donnee pour cette periode</div>;
  }
  const formatted = data.map(d => ({
    ...d,
    label: new Date(d.ts).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }),
  }));
  return (
    <ResponsiveContainer width="100%" height={160}>
      <AreaChart data={formatted}>
        <defs>
          <linearGradient id="gradTotal" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="gradHigh" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f97316" stopOpacity={0.4} />
            <stop offset="100%" stopColor="#f97316" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
        <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748b" }} />
        <YAxis tick={{ fontSize: 10, fill: "#64748b" }} allowDecimals={false} />
        <Tooltip
          contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: "8px", fontSize: 12 }}
          labelStyle={{ color: "#94a3b8" }}
        />
        <Area type="monotone" dataKey="total" stroke="#3b82f6" fill="url(#gradTotal)" strokeWidth={2} name="Total" />
        <Area type="monotone" dataKey="high" stroke="#f97316" fill="url(#gradHigh)" strokeWidth={2} name="HIGH" />
        <Line type="monotone" dataKey="critical" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} name="CRITICAL" />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function TopTable({ title, data, keyField, countField, nameField, testId }) {
  if (!data || data.length === 0) return null;
  return (
    <div data-testid={testId} className="border border-slate-700 rounded-lg overflow-hidden">
      <div className="px-4 py-2 bg-slate-800 text-xs text-slate-400 uppercase tracking-wider font-semibold">{title}</div>
      <div className="divide-y divide-slate-800">
        {data.map((item, i) => (
          <div key={i} className="flex justify-between items-center px-4 py-2 text-sm hover:bg-slate-800/50 transition-colors">
            <span className="text-slate-300 truncate max-w-[200px]" title={item[keyField] || item[nameField] || "—"}>
              {item[nameField] || item[keyField] || "—"}
            </span>
            <span className="font-mono text-slate-400 text-xs ml-4">{item[countField]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SecurityDashboard({ authUser }) {
  const [window, setWindow] = useState("1h");
  const [summary, setSummary] = useState(null);
  const [timeseries, setTimeseries] = useState([]);
  const [alertStatus, setAlertStatus] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [eventsSkip, setEventsSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ severity: "", tenant_id: "", route: "", env: "", version: "" });
  const [versions, setVersions] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [highlightTrace, setHighlightTrace] = useState(null);
  const lastFetch = useRef(0);
  const highlightRef = useRef(null);

  // Deep-link: read trace_id from URL params on mount
  useEffect(() => {
    const params = new URLSearchParams(globalThis.location?.search || "");
    const traceFromUrl = params.get("trace_id");
    if (traceFromUrl) {
      setHighlightTrace(traceFromUrl);
      setWindow("24h"); // widen window to find the event
    }
  }, []);

  // Auto-scroll to highlighted row
  useEffect(() => {
    if (highlightTrace && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [events, highlightTrace]);

  const buildEventLink = (traceId) => {
    const base = globalThis.location?.origin + globalThis.location?.pathname;
    return `${base}?trace_id=${traceId}`;
  };

  const getCookie = (name) => {
    const m = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
    return m ? m[2] : "";
  };

  const fetchAll = useCallback(async (resetSkip = true) => {
    const token = getCookie("session_token");
    const headers = { Cookie: `session_token=${token}` };
    const opts = { credentials: "include", headers };
    if (resetSkip) setEventsSkip(0);
    setLoading(true);

    const qs = `window=${window}` +
      (filters.env ? `&env=${filters.env}` : "") +
      (filters.version ? `&version=${filters.version}` : "");
    const evQs = `limit=30&skip=${resetSkip ? 0 : eventsSkip}` +
      (filters.severity ? `&severity=${filters.severity}` : "") +
      (filters.tenant_id ? `&tenant_id=${filters.tenant_id}` : "") +
      (filters.route ? `&route=${filters.route}` : "") +
      (filters.env ? `&env=${filters.env}` : "") +
      (filters.version ? `&version=${filters.version}` : "");

    try {
      const [sumRes, tsRes, alertRes, evRes, verRes] = await Promise.all([
        authFetch(`${API}/admin/security-events/summary?${qs}`, opts),
        authFetch(`${API}/admin/security-events/timeseries?${qs}&bucket=${window === "5m" ? "1m" : window === "1h" ? "5m" : "1h"}`, opts),
        authFetch(`${API}/admin/security-events/alert-status`, opts),
        authFetch(`${API}/admin/security-events?${evQs}`, opts),
        authFetch(`${API}/admin/security-events/versions`, opts),
      ]);

      if (sumRes.ok) setSummary(await sumRes.json());
      if (tsRes.ok) setTimeseries((await tsRes.json()).series || []);
      if (alertRes.ok) setAlertStatus(await alertRes.json());
      if (evRes.ok) {
        const d = await evRes.json();
        setEvents(d.events || []);
        setEventsTotal(d.total || 0);
      }
      if (verRes.ok) setVersions((await verRes.json()).versions || []);
    } catch (e) {
      console.error("Security dashboard fetch error:", e);
    } finally {
      setLoading(false);
      lastFetch.current = Date.now();
    }
  }, [window, filters, eventsSkip]);

  useEffect(() => { fetchAll(true); }, [window, filters.severity, filters.env, filters.version]);

  const loadMore = async () => {
    const newSkip = eventsSkip + 30;
    setEventsSkip(newSkip);
    const token = getCookie("session_token");
    const opts = { credentials: "include", headers: { Cookie: `session_token=${token}` } };
    const evQs = `limit=30&skip=${newSkip}` +
      (filters.severity ? `&severity=${filters.severity}` : "") +
      (filters.tenant_id ? `&tenant_id=${filters.tenant_id}` : "") +
      (filters.route ? `&route=${filters.route}` : "") +
      (filters.env ? `&env=${filters.env}` : "") +
      (filters.version ? `&version=${filters.version}` : "");
    try {
      const res = await authFetch(`${API}/admin/security-events?${evQs}`, opts);
      if (res.ok) {
        const d = await res.json();
        setEvents(prev => [...prev, ...(d.events || [])]);
      }
    } catch {}
  };

  const clearFilters = () => setFilters({ severity: "", tenant_id: "", route: "", env: "", version: "" });
  const hasActiveFilters = Object.values(filters).some(Boolean);

  const sev = summary?.counts_by_severity || {};

  return (
    <div data-testid="security-dashboard" className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-red-950 border border-red-800 flex items-center justify-center">
            <Shield className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Security Dashboard</h2>
            <p className="text-xs text-slate-500">Isolation tenant & anomalies</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Alert badge */}
          {alertStatus && (
            <div data-testid="alert-status-badge" className={`px-3 py-1.5 rounded-lg text-xs font-bold border ${
              alertStatus.status === "ALERT"
                ? "bg-red-950 border-red-700 text-red-400"
                : "bg-emerald-950 border-emerald-800 text-emerald-400"
            }`}>
              {alertStatus.status === "ALERT" ? <AlertTriangle className="w-3 h-3 inline mr-1" /> : null}
              {alertStatus.status} {alertStatus.current_rate_5m > 0 ? `(${alertStatus.current_rate_5m}/5min)` : ""}
            </div>
          )}

          {/* Version filter */}
          {versions.length > 0 && (
            <select
              data-testid="version-filter"
              value={filters.version}
              onChange={e => setFilters(f => ({ ...f, version: e.target.value }))}
              className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-xs text-slate-300"
            >
              <option value="">Toutes versions</option>
              {versions.map(v => <option key={v} value={v}>{v?.slice(0, 7)}</option>)}
            </select>
          )}

          {/* Window selector */}
          <div className="flex rounded-lg border border-slate-700 overflow-hidden">
            {WINDOWS.map(w => (
              <button
                key={w.value}
                data-testid={`window-btn-${w.value}`}
                onClick={() => setWindow(w.value)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  window === w.value
                    ? "bg-blue-600 text-white"
                    : "bg-slate-800 text-slate-400 hover:text-white"
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>

          <button
            data-testid="refresh-btn"
            onClick={() => fetchAll(true)}
            className="p-2 rounded-lg bg-slate-800 border border-slate-700 hover:bg-slate-700 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 text-slate-400 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard testId="kpi-total" label="Total events" value={summary?.total_events ?? 0} />
        <KpiCard testId="kpi-high" label="HIGH" value={sev.HIGH ?? 0} severity={sev.HIGH > 0 ? "high" : "ok"} />
        <KpiCard testId="kpi-critical" label="CRITICAL" value={sev.CRITICAL ?? 0} severity={sev.CRITICAL > 0 ? "critical" : "ok"} />
        <KpiCard testId="kpi-tenants" label="Tenants touches" value={summary?.unique_tenants ?? 0} />
        <KpiCard testId="kpi-routes" label="Routes touchees" value={summary?.unique_routes ?? 0} />
      </div>

      {/* Alert status panel */}
      {alertStatus && (alertStatus.alert_count_last_24h > 0 || alertStatus.current_rate_5m > 0) && (
        <div data-testid="alert-panel" className="p-4 rounded-lg border border-amber-800 bg-amber-950/30">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <span className="text-sm font-semibold text-amber-300">Alertes recentes</span>
          </div>
          <div className="grid grid-cols-3 gap-4 text-xs">
            <div>
              <div className="text-slate-500">Derniere alerte</div>
              <div className="text-amber-300 font-mono">
                {alertStatus.last_alert_at ? new Date(alertStatus.last_alert_at).toLocaleString("fr-FR") : "Aucune"}
              </div>
            </div>
            <div>
              <div className="text-slate-500">Alertes (24h)</div>
              <div className="text-amber-300 font-mono">{alertStatus.alert_count_last_24h}</div>
            </div>
            <div>
              <div className="text-slate-500">Taux actuel (5min)</div>
              <div className="text-amber-300 font-mono">
                {alertStatus.current_rate_5m} / {alertStatus.threshold} (seuil)
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mini Chart */}
      <div className="border border-slate-700 rounded-lg p-4 bg-slate-900/50">
        <div className="text-xs text-slate-500 uppercase tracking-wider mb-3">Events par periode</div>
        <MiniChart data={timeseries} />
      </div>

      {/* Top Tables */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <TopTable testId="top-tenants" title="Top Tenants" data={summary?.top_tenants} keyField="tenant_id" nameField="name" countField="count" />
        <TopTable testId="top-routes" title="Top Routes" data={summary?.top_routes} keyField="route" nameField="route" countField="count" />
        <TopTable testId="top-collections" title="Top Collections" data={summary?.top_collections} keyField="collection" nameField="collection" countField="count" />
      </div>

      {/* Events Table */}
      <div className="border border-slate-700 rounded-lg overflow-hidden">
        {/* Filters */}
        <div className="px-4 py-3 bg-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-300">Derniers evenements</span>
            <span className="text-xs text-slate-500 font-mono">({eventsTotal} total)</span>
          </div>
          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <button onClick={clearFilters} className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1">
                <XCircle className="w-3 h-3" /> Reset
              </button>
            )}
            <button
              data-testid="toggle-filters-btn"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-1 px-2 py-1 text-xs bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors"
            >
              <Filter className="w-3 h-3" /> Filtres <ChevronDown className={`w-3 h-3 transition-transform ${showFilters ? "rotate-180" : ""}`} />
            </button>
          </div>
        </div>

        {showFilters && (
          <div data-testid="filters-panel" className="px-4 py-3 bg-slate-800/50 border-t border-slate-700 flex flex-wrap gap-3">
            <select
              data-testid="filter-severity"
              value={filters.severity}
              onChange={e => setFilters(f => ({ ...f, severity: e.target.value }))}
              className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-xs text-slate-300"
            >
              <option value="">Severite</option>
              {["CRITICAL", "HIGH", "MEDIUM", "LOW", "INFO"].map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <input
              data-testid="filter-tenant"
              placeholder="Tenant ID..."
              value={filters.tenant_id}
              onChange={e => setFilters(f => ({ ...f, tenant_id: e.target.value }))}
              className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-xs text-slate-300 w-32"
            />
            <input
              data-testid="filter-route"
              placeholder="Route..."
              value={filters.route}
              onChange={e => setFilters(f => ({ ...f, route: e.target.value }))}
              className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-xs text-slate-300 w-32"
            />
            <input
              data-testid="filter-env"
              placeholder="Environment..."
              value={filters.env}
              onChange={e => setFilters(f => ({ ...f, env: e.target.value }))}
              className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-xs text-slate-300 w-28"
            />
            <button
              data-testid="apply-filters-btn"
              onClick={() => fetchAll(true)}
              className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 rounded text-white hover:bg-blue-500"
            >
              <Search className="w-3 h-3" /> Appliquer
            </button>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700 text-xs text-slate-500 uppercase">
                <th className="px-4 py-2 text-left">Heure</th>
                <th className="px-3 py-2 text-left">Severite</th>
                <th className="px-3 py-2 text-left">Tenant</th>
                <th className="px-3 py-2 text-left">Route</th>
                <th className="px-3 py-2 text-left">Collection / Op</th>
                <th className="px-3 py-2 text-left">Env</th>
                <th className="px-3 py-2 text-left">Version</th>
                <th className="px-3 py-2 text-left">Trace</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {events.map((ev, i) => {
                const isHighlighted = highlightTrace && ev.trace_id === highlightTrace;
                return (
                <tr
                  key={i}
                  ref={isHighlighted ? highlightRef : null}
                  className={`transition-colors ${isHighlighted ? "bg-blue-950/60 ring-1 ring-blue-600/50" : "hover:bg-slate-800/50"}`}
                  data-testid={`event-row-${i}`}
                >
                  <td className="px-4 py-2 text-xs text-slate-400 font-mono whitespace-nowrap">
                    {ev.ts ? new Date(ev.ts).toLocaleString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—"}
                  </td>
                  <td className="px-3 py-2"><SeverityBadge severity={ev.severity} /></td>
                  <td className="px-3 py-2 text-xs text-slate-400 max-w-[120px] truncate" title={ev.tenant_id}>
                    {ev.tenant_id ? ev.tenant_id.slice(0, 12) : "—"}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-300 max-w-[160px] truncate font-mono" title={ev.route}>
                    {ev.method && <span className="text-slate-600 mr-1">{ev.method}</span>}
                    {ev.route || "—"}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-400">
                    <span className="text-slate-300">{ev.collection || "—"}</span>
                    {ev.operation && <span className="text-slate-600 ml-1">/ {ev.operation}</span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">{ev.environment || "—"}</td>
                  <td className="px-3 py-2 text-xs text-slate-500 font-mono">
                    {ev.app_version ? (
                      <span className="flex items-center gap-1">
                        {ev.app_version.slice(0, 7)}
                        <CopyButton text={ev.app_version} />
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600 font-mono">
                    {ev.trace_id ? (
                      <span className="flex items-center gap-1">
                        {ev.trace_id}
                        <CopyButton text={ev.trace_id} />
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(buildEventLink(ev.trace_id));
                            setHighlightTrace(ev.trace_id);
                          }}
                          className="p-1 hover:bg-slate-700 rounded transition-colors"
                          title="Copier le lien"
                          data-testid={`copy-link-${i}`}
                        >
                          <Link2 className="w-3 h-3 text-blue-500/60 hover:text-blue-400" />
                        </button>
                      </span>
                    ) : "—"}
                  </td>
                </tr>
                );
              })}
              {events.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-slate-600 text-sm">
                    Aucun evenement pour les filtres selectionnes
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Load more */}
        {events.length < eventsTotal && (
          <div className="px-4 py-3 border-t border-slate-700 text-center">
            <button
              data-testid="load-more-btn"
              onClick={loadMore}
              className="text-xs text-blue-400 hover:text-blue-300 font-medium"
            >
              Charger plus ({eventsTotal - events.length} restants)
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
